// The consent question is only ever put to a client on a page where nothing
// time-sensitive is going on. This is an allowlist: a route that is not named
// here never shows it, so a page added later is safe by default.
//
// client-booking is the client home (upcoming sessions and the sessions list);
// therapist-recommendations is where a self-paying client lands right after
// verifying their email, before they have chosen anyone.
export const AD_CONSENT_PROMPT_ROUTES: readonly string[] = [
   'client-booking',
   'therapist-recommendations',
];

// A query string that says the page is mid-task even though the route is calm:
// returning from a payment, finishing a booking action, or changing therapist.
const BUSY_QUERY_KEYS: Record<string, readonly string[]> = {
   'client-booking': ['checkout_session_id', 'action'],
   'therapist-recommendations': ['redirect'],
};

// The first signed-out step of onboarding. A visitor is asked here, once, before
// anything later in the journey can load a tracker. Nothing else signed-out asks.
export const AD_CONSENT_JOURNEY_ROUTES: readonly string[] = ['auth-client-register-coverage'];

// The first wording that asked about advertising on its own. An older yes covered
// analytics only and is not an answer to this question.
export const AD_CONSENT_MIN_VERSION = 2;

export type AdConsentRouteQuery = Record<string, unknown> | null | undefined;

export const isAdConsentPromptRoute = (
   routeName: string | null | undefined,
   query?: AdConsentRouteQuery,
): boolean => {
   if (!routeName || !AD_CONSENT_PROMPT_ROUTES.includes(routeName)) return false;
   const busy = BUSY_QUERY_KEYS[routeName] ?? [];
   return !busy.some((key) => query?.[key] !== undefined);
};

export interface AdConsentPromptContext {
   routeName: string | null | undefined;
   query?: AdConsentRouteQuery;
   authenticated: boolean;
   impersonating: boolean;
   // Changes when a different client signs in; used to forget the last client's answer.
   accountKey: string | null;
   // A signed-out visitor's own anonymous journey (id and owner token both present),
   // or null. It is what the signed-out answer is stored against.
   journeyKey?: string | null;
   // True while something else owns the screen (welcome, app prompt, feedback, a dialog).
   isBlocked: () => boolean;
}

export type AdConsentAudience = 'client' | 'journey';

export const adConsentAudience = (ctx: Pick<AdConsentPromptContext, 'authenticated'>): AdConsentAudience =>
   ctx.authenticated ? 'client' : 'journey';

// Whether this person, here, is someone the question may be put to at all.
// Decided before the server is even asked. A signed-in client is never asked on
// the onboarding step, and a visitor is never asked anywhere else.
export const canAskAdConsent = (
   ctx: Pick<
      AdConsentPromptContext,
      'routeName' | 'query' | 'authenticated' | 'impersonating' | 'journeyKey'
   >,
): boolean => {
   if (ctx.authenticated) return !ctx.impersonating && isAdConsentPromptRoute(ctx.routeName, ctx.query);
   return !!ctx.journeyKey && !!ctx.routeName && AD_CONSENT_JOURNEY_ROUTES.includes(ctx.routeName);
};

// Whether a visitor's saved answer should be read here, without asking. A reload in the
// middle of onboarding has to find the yes again, or the rest of the journey goes unseen.
export const canReadJourneyConsent = (
   ctx: Pick<AdConsentPromptContext, 'routeName' | 'authenticated' | 'journeyKey'>,
): boolean =>
   !ctx.authenticated &&
   !!ctx.journeyKey &&
   (ctx.routeName === 'auth-client-signup' || !!ctx.routeName?.startsWith('auth-client-register-'));

// Whether the server already holds a real answer to this question, for instance a
// yes or no carried over from the landing page. Such an answer is respected and
// the visitor is not asked again.
export const hasAdChoice = (consent: { updated_at?: number | null; version?: number } | null) =>
   !!consent && consent.updated_at != null && (consent.version ?? 0) >= AD_CONSENT_MIN_VERSION;

// Only an explicit yes from the server on both fields allows tracking.
export const adTrackingAllowed = (
   consent: { granted?: boolean; tracking_allowed?: boolean } | null,
): boolean => !!consent && consent.granted === true && consent.tracking_allowed === true;
